/**
 * Labs API Service
 * Manages lab templates and submissions
 */

import { labManagerApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';
import { requirePaginatedResponse } from './responseValidation';
import type { Lab, LabDetail, PaginatedResponse } from '../types';

interface LabListParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  isPublished?: boolean;
  sortBy?: 'title' | 'createdAt' | 'studentsCompleted' | 'averageScore';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Get paginated list of labs
 */
export async function getLabs(params: LabListParams = {}): Promise<PaginatedResponse<Lab>> {
  try {
    const { isPublished, ...filters } = params;
    const response = await labManagerApi.get('/instructor/labs', { params: { ...filters, published: isPublished } });
    return requirePaginatedResponse<Lab>(response.data, 'labs', ['id', 'title']);
  } catch (error) {
    console.error('Failed to fetch labs:', error);
    // Return mock data for development
    const mockLabs: Lab[] = [
      {
        id: '1',
        title: 'Introduction to OpenAI API',
        description: 'Learn the basics of working with OpenAI\'s GPT models',
        courseId: 'c1',
        courseTitle: 'LLM Fundamentals',
        category: 'LLM Basics',
        difficulty: 'beginner',
        estimatedDuration: 45,
        isPublished: true,
        studentsCompleted: 45,
        studentsInProgress: 8,
        submissionsPending: 3,
        averageScore: 87,
        passRate: 92,
        createdAt: '2024-01-10T10:00:00Z',
        updatedAt: '2024-02-15T14:00:00Z',
      },
      {
        id: '2',
        title: 'Building RAG Applications',
        description: 'Implement retrieval-augmented generation from scratch',
        courseId: 'c2',
        courseTitle: 'AI Applications',
        category: 'Applications',
        difficulty: 'intermediate',
        estimatedDuration: 90,
        isPublished: true,
        studentsCompleted: 28,
        studentsInProgress: 12,
        submissionsPending: 5,
        averageScore: 78,
        passRate: 85,
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-02-20T11:00:00Z',
      },
      {
        id: '3',
        title: 'Fine-tuning Language Models',
        description: 'Learn to fine-tune models for specific tasks',
        courseId: 'c3',
        courseTitle: 'Advanced AI',
        category: 'Model Training',
        difficulty: 'advanced',
        estimatedDuration: 120,
        isPublished: true,
        studentsCompleted: 15,
        studentsInProgress: 8,
        submissionsPending: 4,
        averageScore: 72,
        passRate: 78,
        createdAt: '2024-02-01T10:00:00Z',
        updatedAt: '2024-03-01T09:00:00Z',
      },
      {
        id: '4',
        title: 'Multi-Agent Systems',
        description: 'Build systems with multiple cooperating AI agents',
        courseId: 'c4',
        courseTitle: 'AI Agents',
        category: 'Agents',
        difficulty: 'advanced',
        estimatedDuration: 150,
        isPublished: false,
        studentsCompleted: 0,
        studentsInProgress: 0,
        submissionsPending: 0,
        averageScore: 0,
        passRate: 0,
        createdAt: '2024-03-01T10:00:00Z',
        updatedAt: '2024-03-10T15:00:00Z',
      },
      {
        id: '5',
        title: 'LangChain Fundamentals',
        description: 'Master the LangChain framework for building AI applications',
        courseId: 'c2',
        courseTitle: 'AI Applications',
        category: 'Frameworks',
        difficulty: 'intermediate',
        estimatedDuration: 60,
        isPublished: true,
        studentsCompleted: 38,
        studentsInProgress: 6,
        submissionsPending: 2,
        averageScore: 85,
        passRate: 90,
        createdAt: '2024-01-20T10:00:00Z',
        updatedAt: '2024-02-25T16:00:00Z',
      },
    ];

    let filtered = mockLabs;

    if (params.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(
        (l) => l.title.toLowerCase().includes(search) || l.description.toLowerCase().includes(search)
      );
    }

    if (params.category) {
      filtered = filtered.filter((l) => l.category === params.category);
    }

    if (params.difficulty) {
      filtered = filtered.filter((l) => l.difficulty === params.difficulty);
    }

    if (params.isPublished !== undefined) {
      filtered = filtered.filter((l) => l.isPublished === params.isPublished);
    }

    const mock = {
      data: filtered,
      pagination: {
        page: params.page || 1,
        limit: params.limit || 20,
        total: filtered.length,
        totalPages: Math.ceil(filtered.length / (params.limit || 20)),
      },
    };
    return mockFallback(error, mock);
  }
}

/**
 * Get lab details by ID
 */
export async function getLabDetail(labId: string): Promise<LabDetail> {
  try {
    const response = await labManagerApi.get(`/labs/${labId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch lab detail:', error);
    throw error;
  }
}

/**
 * Create a new lab
 */
export async function createLab(data: Partial<Lab>): Promise<Lab> {
  const response = await labManagerApi.post('/labs', data);
  return response.data;
}

/**
 * Update a lab
 */
export async function updateLab(labId: string, data: Partial<Lab>): Promise<Lab> {
  const response = await labManagerApi.patch(`/labs/${labId}`, data);
  return response.data;
}

/**
 * Delete a lab
 */
export async function deleteLab(labId: string): Promise<void> {
  await labManagerApi.delete(`/labs/${labId}`);
}

/**
 * Publish or unpublish a lab
 */
export async function toggleLabPublish(labId: string, publish: boolean): Promise<Lab> {
  const response = await labManagerApi.post(`/labs/${labId}/publish`, { publish });
  return response.data;
}

/**
 * Get lab categories
 */
export async function getLabCategories(): Promise<string[]> {
  try {
    const response = await labManagerApi.get('/labs/categories');
    return response.data.categories;
  } catch (error) {
    const mock = ['LLM Basics', 'Applications', 'Model Training', 'Agents', 'Frameworks', 'Security'];
    return mockFallback(error, mock);
  }
}

/**
 * Get lab rubric
 */
export async function getLabRubric(labId: string) {
  const response = await labManagerApi.get(`/labs/${labId}/rubric`);
  return response.data;
}

/**
 * Update lab rubric
 */
export async function updateLabRubric(labId: string, rubric: unknown) {
  const response = await labManagerApi.put(`/labs/${labId}/rubric`, rubric);
  return response.data;
}
